import { FC } from 'react';
import ReactSelect, {
    Props as ReactSelectProps,
    components,
    ControlProps,
    ContainerProps,
    MenuProps,
} from 'react-select';
import Label from '@components/InputLabel/InputLabel';
import cx from 'classnames';
import { ash, grey, navy, slate, tealLight, greyDark, azure } from '@variables/colors';

type Props = {
    testId?: string;
    label?: string;
};

//Since this is a third party component, the styling API is different than our other components. For more information on how to style this component, see: https://react-select.com/styles
const optionBackgroundColorStyle = (status) => {
    if (status.isSelected) {
        return `${azure}`;
    } else if (status.isFocused) {
        return `${azure}`;
    } else if (status.isActive) {
        return `${azure}`;
    } else {
        return 'white';
    }
};

const controlBoxShadowStyle = (status) => {
    if (status.isFocused) {
        return `0 0 0 1px ${navy}`;
    } else {
        return 'none';
    }
};

const controlBorderColorStyle = (status) => {
    if (status.isFocused || status.isActive) {
        return `${navy}`;
    } else {
        return `${ash}`;
    }
};

const styles = {
    container: (provided) => ({
        ...provided,
        width: '100%',
    }),
    control: (provided, state) => ({
        ...provided,
        borderColor: controlBorderColorStyle(state),
        fontSize: '14px',
        lineHeight: '18px',
        fontStyle: 'normal',
        height: '3rem',
        borderRadius: '0.25rem',
        color: `${greyDark}`,
        boxShadow: controlBoxShadowStyle(state),
        outline: 'none',
        '&:hover': {
            borderColor: `${navy}`,
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: `${grey}`,
    }),
    menuList: (provided) => ({
        ...provided,
        maxHeight: '16rem',
    }),
    option: (provided, state) => ({
        ...provided,
        color: `${slate}`,
        backgroundColor: optionBackgroundColorStyle(state),
        zIndex: '1',
        '&:active': {
            backgroundColor: `${tealLight}`,
        },
    }),
    valueContainer: (provided, state) => ({
        ...provided,
        zIndex: '1',
    }),
};

const SelectContainer: FC<ContainerProps> = ({ children, className, innerProps, selectProps, ...props }) => {
    return (
        <components.SelectContainer
            {...props}
            // @ts-expect-error: Need to access div element's data attribute directly to add data-testid
            innerProps={{ ['data-testid']: selectProps.testId, ...innerProps }}
        >
            {children}
        </components.SelectContainer>
    );
};

const Control: FC<ControlProps> = ({ children, innerProps, ...props }) => {
    return (
        <components.Control
            {...props}
            // @ts-expect-error: Need to access div element's data attribute directly to add data-testid
            innerProps={{ ['data-testid']: 'react-select-control', ...innerProps }}
        >
            {children}
        </components.Control>
    );
};

const Menu: FC<MenuProps> = ({ children, innerProps, className, ...props }) => {
    return (
        <components.Menu
            {...props}
            className={'tw-relative !tw-z-30'}
            // @ts-expect-error: Need to access div element's data attribute directly to add data-testid
            innerProps={{ ['data-testid']: 'react-select-menu', ...innerProps }}
        >
            {children}
        </components.Menu>
    );
};

const Select: FC<Props & ReactSelectProps> = (props) => {
    return (
        <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-items-start tw-justify-start">
            <Label htmlFor={props.name} testId={`${props.testId}-label`}>
                {props.label}
            </Label>
            <ReactSelect
                styles={styles}
                {...props}
                className={cx({ [`${props.testId}`]: !!props.testId })}
                menuPortalTarget={props.menuPortalTarget}
                menuPlacement={props.menuPlacement}
                components={{ Control, SelectContainer, Menu }}
            />
        </div>
    );
};

export default Select;
